import * as _ from 'lodash';
import * as config from './config';
import { promises as dns } from 'dns';
import { getLogger } from './logger';
import { promiseMap } from '../utils/promiseUtils';

const logger = getLogger('http-request-counters');

let networkConnectivityTestFailed = false;

/** Tests network connectivity with DNS resolution (a basic test for a 7 later stack parallel but essential to most HTTP requests) */
const testNetworkConnectivity = async () => {
    if (config.IS_ON_PREM) {
        return true;
    }
    const hostnames = ['www.google.com', 'www.facebook.com', 'www.microsoft.com', 'testim.io'];
    try {
        // If any of these domains resolve we consider the connectivity to be ok
        const result = Boolean(await promiseMap(hostnames, host => dns.lookup(host)));
        if (!result) {
            networkConnectivityTestFailed = true;
        }
        return result;
    } catch (e) {
        logger.error('network connectivity test failed');
        networkConnectivityTestFailed = true;
        return false;
    }
};
const throttledTestNetworkConnectivity = _.throttle(testNetworkConnectivity, 10 * 1000);

// we remove entries after 15 minutes, note that this isn't accurate because
// we remove the "fail"/"success" 10 minutes after we add them (and not the "call")
// this is fine since these values are an estimation and not an accurate representation
// we poll them when a test fails - so values older than 15 minutes (10 minutes is
// the default timeout) are hardly relevant.
const ttl = 60 * 1000 * 15;

export const makeCounters = () => {
    const counters = {
        call: new Map<string, number>(),
        success: new Map<string, number>(),
        fail: new Map<string, number>(),
    };
    function update(counter: Map<string, number>, key: string) {
        const result = counter.get(key) || 0;
        counter.set(key, result + 1);
        setTimeout(() => {
            const _result = counter.get(key) || 1;
            counter.set(key, _result - 1);
        }, ttl);
    }
    function wrapWithMonitoring<F extends(...arg: any) => PromiseLike<any>>(fn: F, name: string = fn.name) {
        return async function<T = Awaited<ReturnType<F>>> (...args: Parameters<F>): Promise<T> {
            update(counters.call, name);
            try {
                const result = await fn.call(null, ...args);
                update(counters.success, name);
                return result;
            } catch (e) {
                update(counters.fail, name);
                if (!networkConnectivityTestFailed) {
                    throttledTestNetworkConnectivity();
                }
                throw e;
            }
        };
    }
    async function isNetworkHealthy() {
        if (networkConnectivityTestFailed || !(await testNetworkConnectivity())) {
            return false;
        }
        const allFailed = _.sum([...counters.fail.values()]);
        const allCalls = _.sum([...counters.call.values()]);
        // we declare a test unhealthy network wise if
        // 10% or more of requests (out of finished requests) failed
        // note that the network can be unhealthy but the test would still pass
        return allFailed < allCalls * 0.1;
    }
    isNetworkHealthy.counters = counters;
    wrapWithMonitoring.counters = counters; // expose the counters used to the outside
    wrapWithMonitoring.isNetworkHealthy = isNetworkHealthy;
    wrapWithMonitoring.didNetworkConnectivityTestFail = () => networkConnectivityTestFailed;
    return wrapWithMonitoring;
};
